// Détection du contexte (marketing sur www/apex vs espace mariage sur un sous-domaine).
// Doit rester cohérent avec server/middleware/tenant.ts

const ROOT_DOMAIN = "please-rsvp.com";

const RESERVED = new Set([
  "www",
  "app",
  "api",
  "admin",
  "mail",
  "static",
  "assets",
  "cdn",
  "blog",
  "help",
  "support",
  "status",
  "dashboard",
]);

export function getSubdomain(): string | null {
  const hostname = window.location.hostname.toLowerCase();

  // Override de dev : ?tenant=foo (pour le chargement courant uniquement).
  const params = new URLSearchParams(window.location.search);
  const override = params.get("tenant");
  if (override) return override;

  // Support local : foo.localhost → "foo" (les navigateurs résolvent *.localhost
  // vers la boucle locale). Doit passer avant le test `=== "localhost"`.
  if (hostname.endsWith(".localhost")) {
    const sub = hostname.slice(0, -".localhost".length).split(".")[0];
    return sub && !RESERVED.has(sub) ? sub : null;
  }

  // localhost nu / IP = contexte marketing (utiliser foo.localhost pour un tenant).
  if (hostname === "localhost" || /^\d+\.\d+\.\d+\.\d+$/.test(hostname)) {
    return null;
  }

  if (hostname.endsWith(".lvh.me")) {
    const sub = hostname.slice(0, -".lvh.me".length).split(".")[0];
    return sub && !RESERVED.has(sub) ? sub : null;
  }

  if (hostname === ROOT_DOMAIN) return null;

  if (hostname.endsWith("." + ROOT_DOMAIN)) {
    const sub = hostname.slice(0, -(ROOT_DOMAIN.length + 1)).split(".")[0];
    return sub && !RESERVED.has(sub) ? sub : null;
  }

  return null;
}

export function isMarketing(): boolean {
  return getSubdomain() === null;
}

// Le manifeste est résolu côté serveur d'après le Host. En dev, le tenant peut
// venir de `?tenant=foo` : le navigateur demande le manifeste sans la query
// string, donc on la lui repasse explicitement.
export function syncManifestTenant(): void {
  const override = new URLSearchParams(window.location.search).get('tenant');
  if (!override) return;
  const link = document.querySelector<HTMLLinkElement>('link[rel="manifest"]');
  if (link) link.href = `/api/manifest.webmanifest?tenant=${encodeURIComponent(override)}`;
}

// URL absolue vers un sous-domaine donné (pour rediriger après inscription/connexion)
export function subdomainUrl(subdomain: string, path = "/"): string {
  const hostname = window.location.hostname.toLowerCase();
  const protocol = window.location.protocol;
  const port = window.location.port ? `:${window.location.port}` : "";

  // En dev sur *.localhost / localhost : on redirige vers foo.localhost:port
  if (hostname === "localhost" || hostname.endsWith(".localhost")) {
    return `${protocol}//${subdomain}.localhost${port}${path}`;
  }
  if (/^\d+\.\d+\.\d+\.\d+$/.test(hostname)) {
    // Sur une IP brute : on garde le même host et on passe le tenant en query
    return `${protocol}//${hostname}${port}${path}?tenant=${subdomain}`;
  }
  if (hostname.endsWith(".lvh.me") || hostname === "lvh.me") {
    return `${protocol}//${subdomain}.lvh.me${port}${path}`;
  }
  return `${protocol}//${subdomain}.${ROOT_DOMAIN}${path}`;
}

// URL absolue vers le site marketing (apex/www) selon l'environnement courant.
export function marketingUrl(path = "/"): string {
  const hostname = window.location.hostname.toLowerCase();
  const protocol = window.location.protocol;
  const port = window.location.port ? `:${window.location.port}` : "";

  if (
    hostname === "localhost" ||
    hostname.endsWith(".localhost") ||
    /^\d+\.\d+\.\d+\.\d+$/.test(hostname)
  ) {
    return `${protocol}//localhost${port}${path}`;
  }
  if (hostname.endsWith(".lvh.me") || hostname === "lvh.me") {
    return `${protocol}//lvh.me${port}${path}`;
  }
  return `https://www.${ROOT_DOMAIN}${path}`;
}
